import type {
  CampaignApi,
  CampaignAudienceApi,
  CampaignListStatsApi,
  CampaignContentVersionApi,
  CampaignDetailResponseApi,
  CampaignSummaryApi,
  CreateCampaignRequestApi,
  SetCampaignAudienceRequestApi,
  UpdateCampaignRequestApi,
} from '../types/campaigns-api.types';
import type {
  CampaignContentVersion,
  CampaignListStats,
  CampaignRestAudience,
  CampaignRestDetail,
  CampaignRestSummary,
  CreateCampaignRestInput,
  SetCampaignAudienceInput,
  UpdateCampaignRestInput,
} from '../types/campaigns-rest.types';
import type {
  Campaign,
  CampaignDisplayStatus,
  CampaignStatus,
  CampaignType,
} from '../types/campaigns.types';
import { formatCampaignDate } from './campaigns-mapper.utils';
import { fromDraftScheduleApi, toDraftScheduleRequestApi } from './campaign-schedule-draft.utils';

const filterStrings = (values: string[] | null | undefined): string[] =>
  (values ?? []).map((value) => value?.trim()).filter((value): value is string => Boolean(value));

const trimOrUndefined = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const trimOrNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed || null;
};

const trimOrEmpty = (value: string | null | undefined): string => value?.trim() ?? '';

const mapAudienceApi = (audience: CampaignAudienceApi | null | undefined): CampaignRestAudience => ({
  listIds: filterStrings(audience?.listIds),
  segmentIds: filterStrings(audience?.segmentIds),
  excludeListIds: filterStrings(audience?.excludeListIds),
  excludeSegmentIds: filterStrings(audience?.excludeSegmentIds),
  estimatedRecipients:
    typeof audience?.estimatedRecipients === 'number' ? audience.estimatedRecipients : null,
  sendableCount: typeof audience?.sendableCount === 'number' ? audience.sendableCount : null,
  estimatedAt: audience?.estimatedAt?.trim() || null,
});

export const normalizeCampaignStatus = (raw: string | null | undefined): CampaignStatus => {
  const normalized = raw?.trim().toLowerCase() ?? '';
  // Only true drafts are editable; everything else is treated as non-editable.
  if (!normalized || normalized === 'draft') return 'Draft';
  return 'Published';
};

export const mapApiStatusToDisplayStatus = (
  raw: string | null | undefined
): CampaignDisplayStatus => {
  switch (raw?.trim().toLowerCase()) {
    case 'scheduled':
      return 'Scheduled';
    case 'sending':
      return 'Sending';
    case 'sent':
    case 'published':
    case 'completed':
    case 'archived':
      return 'Sent';
    case 'failed':
      return 'Failed';
    case 'cancelled':
    case 'canceled':
      return 'Cancelled';
    case 'draft':
    default:
      return 'Draft';
  }
};

export const mapCampaignApiToRestDetail = (
  campaign: CampaignApi,
  extras?: {
    contentHasHtml?: boolean;
    contentId?: string | null;
  }
): CampaignRestDetail | null => {
  const id = campaign.itemId?.trim();
  if (!id) return null;

  const audience = mapAudienceApi(campaign.campaignAudience);
  const audienceSize = audience.estimatedRecipients;
  const sendableCount = audience.sendableCount;

  const contentIds = filterStrings(campaign.contentIds);
  const summaryContentId = extras?.contentId?.trim();
  if (summaryContentId && !contentIds.includes(summaryContentId)) {
    contentIds.unshift(summaryContentId);
  }

  return {
    id,
    name: trimOrEmpty(campaign.name),
    subject: trimOrEmpty(campaign.subject),
    preheader: trimOrEmpty(campaign.previewText),
    channel: trimOrEmpty(campaign.channel) || 'email',
    status: normalizeCampaignStatus(campaign.status),
    fromName: trimOrEmpty(campaign.fromName),
    fromEmail: trimOrEmpty(campaign.fromEmail),
    fromAddress: trimOrEmpty(campaign.fromAddress ?? campaign.fromEmail),
    replyTo: trimOrEmpty(campaign.replyTo),
    templateIds: filterStrings(campaign.templateIds),
    contentIds,
    tags: filterStrings(campaign.tags),
    audience,
    audienceSize,
    sendableCount,
    contentHasHtml: extras?.contentHasHtml ?? false,
    scheduledAt: campaign.scheduledAt ?? null,
    timeZone: campaign.timeZone ?? null,
    isRecurring: campaign.isRecurring ?? false,
    draftSchedule: fromDraftScheduleApi(campaign.draftSchedule),
    createdAt: campaign.createdAt ?? null,
    updatedAt: campaign.updatedAt ?? null,
  };
};

export const mapCampaignDetailResponse = (
  data: CampaignDetailResponseApi
): CampaignRestDetail | null => {
  if (!data.campaign) return null;

  return mapCampaignApiToRestDetail(data.campaign, {
    contentHasHtml: data.contentSummary?.hasHtml,
    contentId: data.contentSummary?.contentId,
  });
};

const rateOrNull = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** `null` stays `null`: a campaign that has never sent has no stats, which is not stats of zero. */
const mapListStats = (stats: CampaignListStatsApi | null | undefined): CampaignListStats | null =>
  stats
    ? {
        recipients: stats.recipients ?? 0,
        delivered: stats.delivered ?? 0,
        openRate: rateOrNull(stats.openRate),
        clickRate: rateOrNull(stats.clickRate),
      }
    : null;

export const mapCampaignSummaryApi = (row: CampaignSummaryApi): CampaignRestSummary | null => {
  const id = row.itemId?.trim();
  if (!id) return null;

  const summary = row.audienceSummary;
  const audienceSize =
    typeof summary?.estimatedRecipients === 'number' ? summary.estimatedRecipients : null;
  const sendableCount =
    typeof summary?.sendableCount === 'number' ? summary.sendableCount : null;

  return {
    id,
    name: trimOrEmpty(row.name),
    subject: trimOrEmpty(row.subject),
    status: trimOrEmpty(row.status),
    channel: trimOrEmpty(row.channel) || 'email',
    audienceSize,
    sendableCount,
    estimatedAt: summary?.estimatedAt?.trim() || null,
    scheduledAt: row.scheduledAt ?? null,
    sentAt: row.sentAt ?? null,
    createdAt: row.createdAt ?? null,
    updatedAt: row.updatedAt ?? null,
    templateIds: filterStrings(row.templateIds),
    stats: mapListStats(row.stats),
  };
};

export const mapCampaignRestSummaryToCampaign = (row: CampaignRestSummary): Campaign => {
  const displayStatus = mapApiStatusToDisplayStatus(row.status);
  const status: CampaignStatus = displayStatus === 'Draft' ? 'Draft' : 'Published';
  const type: CampaignType =
    displayStatus === 'Scheduled' || Boolean(row.scheduledAt?.trim()) ? 'Scheduled' : 'Regular';
  const showSentAt =
    displayStatus === 'Sent' ||
    displayStatus === 'Sending' ||
    displayStatus === 'Failed' ||
    displayStatus === 'Cancelled';

  return {
    id: row.id,
    name: row.name.trim() || 'Untitled campaign',
    subject: row.subject,
    channel: row.channel.toLowerCase() === 'sms' ? 'SMS' : 'Email',
    displayStatus,
    audienceSize: row.audienceSize,
    sendableCount: row.sendableCount,
    scheduledOrSentAt: formatCampaignDate(
      showSentAt ? row.sentAt ?? row.scheduledAt : row.scheduledAt ?? row.sentAt
    ),
    type,
    status,
    openRate: row.stats?.openRate ?? null,
    clickRate: row.stats?.clickRate ?? null,
  };
};

export const toCreateCampaignRequestApi = (
  input: CreateCampaignRestInput
): CreateCampaignRequestApi => ({
  name: trimOrUndefined(input.name),
  channel: input.channel?.trim() || 'email',
  subject: trimOrUndefined(input.subject),
  previewText: trimOrUndefined(input.preheader),
  fromName: trimOrUndefined(input.fromName),
  fromEmail: trimOrUndefined(input.fromEmail),
  fromAddress: trimOrUndefined(input.fromAddress),
  replyTo: trimOrUndefined(input.replyTo),
  templateId: trimOrUndefined(input.templateId),
});

export const toUpdateCampaignRequestApi = (
  input: UpdateCampaignRestInput
): UpdateCampaignRequestApi => {
  const body: UpdateCampaignRequestApi = {};

  if (input.name !== undefined) body.name = trimOrUndefined(input.name);
  if (input.subject !== undefined) body.subject = trimOrNull(input.subject);
  if (input.preheader !== undefined) body.previewText = trimOrNull(input.preheader);
  if (input.fromName !== undefined) body.fromName = trimOrNull(input.fromName);
  if (input.fromEmail !== undefined) body.fromEmail = trimOrNull(input.fromEmail);
  if (input.fromAddress !== undefined) body.fromAddress = trimOrNull(input.fromAddress);
  if (input.replyTo !== undefined) body.replyTo = trimOrNull(input.replyTo);
  if (input.draftSchedule === null) body.clearDraftSchedule = true;
  else if (input.draftSchedule) body.draftSchedule = toDraftScheduleRequestApi(input.draftSchedule);

  return body;
};

export const toSetCampaignAudienceRequestApi = (
  input: SetCampaignAudienceInput
): SetCampaignAudienceRequestApi => ({
  listIds: filterStrings(input.listIds),
  segmentIds: filterStrings(input.segmentIds),
  excludeListIds: filterStrings(input.excludeListIds),
  excludeSegmentIds: filterStrings(input.excludeSegmentIds),
});

export const mapCampaignContentVersionApi = (
  data: CampaignContentVersionApi
): CampaignContentVersion => {
  const removed = Array.isArray(data.sanitization?.removed)
    ? data.sanitization.removed.filter((item): item is string => Boolean(item?.trim()))
    : [];
  return {
    contentId: data.contentId?.trim() || '',
    version: typeof data.version === 'number' ? data.version : 0,
    html: typeof data.html === 'string' ? data.html : '',
    plainText: data.plainText ?? null,
    links: filterStrings(data.links),
    isCurrent: Boolean(data.isCurrent),
    updatedAt: data.updatedAt ?? null,
    updatedBy: data.updatedBy ?? null,
    sanitization: {
      removed,
      count: typeof data.sanitization?.count === 'number' ? data.sanitization.count : removed.length,
    },
  };
};
